import React, { useRef, useState, useEffect } from 'react'
import { FiArrowRight } from 'react-icons/fi'
import { FaPlay } from 'react-icons/fa'
import EarlyAccessForm from './EarlyAccessForm'

// Dummy card data with sub-cards
export const cardsData = [
  {
    id: 1,
    step: 1,
    title: 'Discover',
    bgColor: '#86A6FF',
    bgColor2: '#6c9dff',
    subCards: [
      {
        id: '1-1',
        title: '',
        bullets: [
          'Missing out on potential leads?',
          'Struggling to get high-value sales signals?',
          'Facing difficulty in identifying the right contacts?'
        ],
        content: ``,
        videoLink: ''
      },
      {
        id: '1-2',
        title: '',
        bullets: [],
        content:
          'Track prospects and target accounts with ease. Our AI agents surface high-value lead signals on leadership, funding, M&A, and strategic shifts, so you never miss an opportunity.',
        keywords: ['Prospect Tracking', 'Lead Signals', 'Opportunities'],
        videoLink: 'link6'
      },
      {
        id: '1-3',
        title: '',
        bullets: [],
        content:
          'Track your focus set of accounts using our account intelligence, and create your own watchlist.',
        keywords: ['Account Intelligence', 'Watchlist', 'Target Accounts'],
        videoLink: 'link5'
      },
      {
        id: '1-4',
        title: '',
        bullets: [],
        content:
          'Discover and connect with the right person with a personalised message.',
        keywords: ['Find people', 'Connect', 'Engage'],
        videoLink: 'link3'
      }
    ]
  },
  {
    id: 2,
    step: 2,
    title: 'Qualify',
    bgColor: '#3250FD',
    bgColor2: '#2330e4',
    subCards: [
      {
        id: '2-1',
        title: '',
        bullets: [
          'Got an opportunity, want to qualify better?',
          'Want to research deeper on the account to align the pitch?',
          'Need to prepare a pre-meeting brief before meeting the client?'
        ],
        content: '',
        videoLink: ''
      },
      {
        id: '2-2',
        title: '',
        bullets: [],
        content:
          'WhiteOwl helps you qualify leads by quickly researching your prospects and clients. It can be about their business, industry, key stakeholders, financials, etc.',
        keywords: ['Lead Qualification', 'Prospect Research', 'Key Stakeholders'],
        videoLink: 'link2'
      },
      {
        id: '2-3',
        title: '',
        bullets: [],
        content:
          'Build a focused opportunity note to analyse client challenges and prepare a pitch aligned with your solutions.',
        keywords: ['Opportunity Note', 'Client Analysis', 'Pitch Preparation'],
        videoLink: 'link1'
      },
      {
        id: '2-4',
        title: '',
        bullets: [],
        content:
          'Uncover valuable insights, go fully prepared, and generate tailored pre-meeting briefs, approach notes in just a few clicks.',
        keywords: ['Insights', 'Approach Notes', 'Efficiency'],
        videoLink: 'link9'
      }
    ]
  },
  {
    id: 3,
    step: 3,
    title: 'Pitch',
    bgColor: '#937BFF',
    bgColor2: '#7a4aff',
    subCards: [
      {
        id: '3-1',
        title: '',
        bullets: [
          'Taking longer to send proposals?',
          'Missing out on client requirements?',
          'Repeating iterations, longer diagnostic phase?'
        ],
        content: '',
        videoLink: ''
      },
      {
        id: '3-2',
        title: '',
        bullets: [],
        content:
          'Add client inputs and project details effortlessly. Generate proposal drafts faster, refine them with AI, handle objections, and build winning proposals with WhiteOwl.',
        keywords: ['Proposal Drafts', 'Objection Handling', 'AI Refinement'],
        videoLink: 'link4'
      },
      {
        id: '3-3',
        title: '',
        bullets: [],
        content:
          'Use built-in Copilot to iterate and make changes, and save hours of re-prompting.',
        keywords: ['Copilot', 'Efficiency', 'Automation'],
        videoLink: 'link8'
      },
      {
        id: '3-4',
        title: '',
        bullets: [],
        content:
          'Set your own templates and generate output as documents or presentations based on your brand guidelines.',
        keywords: ['Templates', 'Brand Consistency', 'Custom Output'],
        videoLink: 'link7'
      }
    ]
  }
]

export default function CarouselCards() {
  const containerRef = useRef(null)
  const activeCardContentRef = useRef(null)
  const [activeCardIndex, setActiveCardIndex] = useState(0)
  const [showFreeAccountForm, setShowFreeAccountForm] = useState(false)
  // Track sub-card index per card independently
  const [subCardIndices, setSubCardIndices] = useState(() => {
    const initial = {}
    cardsData.forEach((card, index) => {
      initial[index] = 0
    })
    return initial
  })
  const [scrollProgress, setScrollProgress] = useState(0)
  const [windowHeight, setWindowHeight] = useState(0)
  const scrollTimeoutRef = useRef(null)
  const currentSnapIndexRef = useRef(0)
  const isScrollingRef = useRef(false)
  const wheelTimeoutRef = useRef(null)
  const lastWheelTimeRef = useRef(0)
  const totalSteps = cardsData.length
  const NAV_HEIGHT = 140 // px, matches inline style height below
  const CIRCLE_SIZE = 24 // px, from w-6 h-6 (6 * 4px)
  const stepGap = totalSteps > 1 ? NAV_HEIGHT / (totalSteps - 1) : 0
  // Move previous circles to touch the boundary exactly (no overlap)
  const previousOffset = Math.max(0, stepGap - CIRCLE_SIZE-5)

  // Handler to change sub-card for a specific card
  const handleSubCardChange = (cardIndex, subCardIndex) => {
    setSubCardIndices(prev => ({
      ...prev,
      [cardIndex]: subCardIndex
    }))
  }

  // Hardcoded video data
  const videoData = {
    link1: 'https://d2s7q4z8t1mini.cloudfront.net/video/link1.mp4',
    link2: 'https://d2s7q4z8t1mini.cloudfront.net/video/link2.mp4',
    link3: 'https://d2s7q4z8t1mini.cloudfront.net/video/link3.mp4',
    link4: 'https://d2s7q4z8t1mini.cloudfront.net/video/link4.mp4',
    link5: 'https://d2s7q4z8t1mini.cloudfront.net/video/link5.mp4',
    link6: 'https://d2s7q4z8t1mini.cloudfront.net/video/link6.mp4',
    link7: 'https://d2s7q4z8t1mini.cloudfront.net/video/link7.mp4',
    link8: 'https://d2s7q4z8t1mini.cloudfront.net/video/link8.mp4',
    link9: 'https://d2s7q4z8t1mini.cloudfront.net/video/link9.mp4'
  }

  useEffect(() => {
    // Set initial window height
    setWindowHeight(window.innerHeight)
    
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])


  useEffect(() => {
    // Calculate snap points - one for each sub-card within each main card
    const calculateSnapPoints = () => {
      if (!containerRef.current || !windowHeight) return []
      
      const containerTop = containerRef.current.offsetTop
      const carouselReadyPoint = containerTop - windowHeight * 0.2
      // Start transitions only when carousel is properly centered (at carouselReadyPoint)
      // Add a small buffer to ensure card is fully in frame before transitions start
      const sectionStart = carouselReadyPoint + (windowHeight * 0.15)
      const scrollPerSubCard = windowHeight * 0.4 // Scroll distance per sub-card
      const cardTransitionBuffer = windowHeight * 0.4
      
      const snapPoints = []
      let currentPosition = sectionStart
      
      // Calculate snap points - one per sub-card within each card
      cardsData.forEach((card, cardIndex) => {
        const subCardsPerCard = card.subCards.length
        
        // Add snap points for each sub-card within this card
        for (let i = 0; i < subCardsPerCard; i++) {
          snapPoints.push(currentPosition)
          // Move to next sub-card position (add buffer after last sub-card if not last card)
          if (i < subCardsPerCard - 1) {
            currentPosition += scrollPerSubCard
          } else if (cardIndex < cardsData.length - 1) {
            // After last sub-card, add buffer before next card's first sub-card
            currentPosition += scrollPerSubCard + cardTransitionBuffer
          } else {
            // Last sub-card of last card
            currentPosition += scrollPerSubCard
          }
        }
      })
      
      return snapPoints
    }

    const findNearestSnapIndex = (scrollTop, snapPoints) => {
      if (snapPoints.length === 0) return 0
      
      let nearestIndex = 0
      let minDistance = Math.abs(scrollTop - snapPoints[0])
      
      for (let i = 1; i < snapPoints.length; i++) {
        const distance = Math.abs(scrollTop - snapPoints[i])
        if (distance < minDistance) {
          minDistance = distance
          nearestIndex = i
        }
      }
      
      return nearestIndex
    }

    const scrollToSnapPoint = (index) => {
      const snapPoints = calculateSnapPoints()
      if (index >= 0 && index < snapPoints.length) {
        currentSnapIndexRef.current = index
        
        // Map snap point index to card and sub-card indices
        let cardIndex = 0
        let subCardIndex = 0
        let currentSnapOffset = 0
        
        for (let i = 0; i < cardsData.length; i++) {
          const subCardsPerCard = cardsData[i].subCards.length
          
          if (index < currentSnapOffset + subCardsPerCard) {
            cardIndex = i
            subCardIndex = index - currentSnapOffset
            break
          }
          
          currentSnapOffset += subCardsPerCard
        }
        
        // Ensure indices are valid
        cardIndex = Math.max(0, Math.min(cardsData.length - 1, cardIndex))
        const currentCard = cardsData[cardIndex]
        subCardIndex = Math.max(0, Math.min(currentCard.subCards.length - 1, subCardIndex))
        
        // Calculate progress for smooth transitions
        const progressWithinCard = subCardIndex / currentCard.subCards.length
        
        // Update state immediately - update both card and sub-card
        setActiveCardIndex(cardIndex)
        setSubCardIndices(prev => ({
          ...prev,
          [cardIndex]: subCardIndex
        }))
        setScrollProgress(cardIndex + progressWithinCard)
        
        isScrollingRef.current = true
        window.scrollTo({
          top: snapPoints[index],
          behavior: 'smooth'
        })
        
        // Reset scrolling flag after animation completes
        setTimeout(() => {
          isScrollingRef.current = false
        }, 500)
      }
    }

    // Handle wheel events for discrete scrolling
    const handleWheel = (e) => {
      if (!containerRef.current || !windowHeight) {
        return
      }
      
      // Block all wheel events if we're currently scrolling
      if (isScrollingRef.current) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      
      const containerTop = containerRef.current.offsetTop
      const carouselReadyPoint = containerTop - windowHeight * 0.2
      // Start transitions only when carousel is properly centered and in frame
      const sectionStart = carouselReadyPoint + (windowHeight * 0.15)
      const scrollPerSubCard = windowHeight * 0.4
      const cardTransitionBuffer = windowHeight * 0.4
      
      // Calculate total scroll distance based on all sub-cards
      const cardScrollDistances = cardsData.map(card => {
        const subCardsPerCard = card.subCards.length
        const scrollPerCard = scrollPerSubCard * subCardsPerCard
        return scrollPerCard + cardTransitionBuffer
      })
      const totalScrollDistance = cardScrollDistances.reduce((sum, dist) => sum + dist, 0)
      const sectionEnd = sectionStart + totalScrollDistance
      
      const currentScroll = window.scrollY
      
      // Only handle wheel events when in carousel section
      if (currentScroll >= sectionStart && currentScroll <= sectionEnd) {
        // Use ref as source of truth for current position (more reliable during transitions)
        const snapPoints = calculateSnapPoints()
        const currentIndex = currentSnapIndexRef.current
        
        // Ensure current index is valid
        if (currentIndex < 0 || currentIndex >= snapPoints.length) {
          // Recalculate if ref is out of bounds
          const currentScrollPos = window.scrollY
          const recalculatedIndex = findNearestSnapIndex(currentScrollPos, snapPoints)
          currentSnapIndexRef.current = recalculatedIndex
          // Allow normal scrolling if index is invalid
          return
        }
        
        // Determine scroll direction
        const deltaY = e.deltaY
        
        // Allow normal page scrolling if at boundaries
        if (deltaY > 0 && currentIndex >= snapPoints.length - 1) {
          // At last card, scrolling down - allow normal page scroll
          return
        }
        if (deltaY < 0 && currentIndex <= 0) {
          // At first card, scrolling up - allow normal page scroll
          return
        }
        
        // Prevent default only if we're going to handle the scroll
        e.preventDefault()
        e.stopPropagation()
        
        // Throttle - ensure enough time has passed since last action
        const now = Date.now()
        const timeSinceLastWheel = now - lastWheelTimeRef.current
        
        // Wait at least 300ms between wheel actions to ensure one card changes at a time
        if (timeSinceLastWheel < 300) {
          return
        }
        
        let nextIndex = currentIndex
        
        // Only move exactly one step at a time
        if (deltaY > 0 && currentIndex < snapPoints.length - 1) {
          // Scroll down - move to next snap point (only +1)
          nextIndex = currentIndex + 1
        } else if (deltaY < 0 && currentIndex > 0) {
          // Scroll up - move to previous snap point (only -1)
          nextIndex = currentIndex - 1
        } else {
          // Can't move in this direction - allow normal scroll
          return
        }
        
        // Ensure we're only moving exactly one step
        if (Math.abs(nextIndex - currentIndex) !== 1) {
          return
        }
        
        // Update timestamp and scroll to next position
        lastWheelTimeRef.current = now
        scrollToSnapPoint(nextIndex)
      }
    }

    const handleScroll = () => {
      if (!containerRef.current || !windowHeight) return
      
      // Don't update during programmatic scrolling
      if (isScrollingRef.current) {
        return
      }
      
      const snapPoints = calculateSnapPoints()
      const currentScroll = window.scrollY
      const nearestIndex = findNearestSnapIndex(currentScroll, snapPoints)
      
      // Update current snap index
      currentSnapIndexRef.current = nearestIndex
      
      const scrollTop = window.scrollY
      const containerTop = containerRef.current.offsetTop
      
      // Define when carousel is properly in view and ready for interactions
      const carouselReadyPoint = containerTop - windowHeight * 0.2
      // Start card transitions only when carousel is properly centered and in frame
      const sectionStart = carouselReadyPoint + (windowHeight * 0.15)
      const scrollPerSubCard = windowHeight * 0.4
      const cardTransitionBuffer = windowHeight * 0.4
      
      // Calculate total scroll distance based on all sub-cards
      const cardScrollDistances = cardsData.map(card => {
        const subCardsPerCard = card.subCards.length
        const scrollPerCard = scrollPerSubCard * subCardsPerCard
        return scrollPerCard + cardTransitionBuffer
      })
      const totalScrollDistance = cardScrollDistances.reduce((sum, dist) => sum + dist, 0)
      const sectionEnd = sectionStart + totalScrollDistance
      
      if (scrollTop >= sectionStart && scrollTop <= sectionEnd) {
        // Map snap point index to card and sub-card indices
        const snapIndex = currentSnapIndexRef.current
        let cardIndex = 0
        let subCardIndex = 0
        let currentSnapOffset = 0
        
        for (let i = 0; i < cardsData.length; i++) {
          const subCardsPerCard = cardsData[i].subCards.length
          
          if (snapIndex < currentSnapOffset + subCardsPerCard) {
            cardIndex = i
            subCardIndex = snapIndex - currentSnapOffset
            break
          }
          
          currentSnapOffset += subCardsPerCard
        }
        
        // Ensure indices are valid
        cardIndex = Math.max(0, Math.min(cardsData.length - 1, cardIndex))
        const currentCard = cardsData[cardIndex]
        subCardIndex = Math.max(0, Math.min(currentCard.subCards.length - 1, subCardIndex))
        
        // Calculate progress for smooth transitions
        const progressWithinCard = subCardIndex / currentCard.subCards.length
        
        // Update both card and sub-card indices
        setActiveCardIndex(cardIndex)
        setSubCardIndices(prev => ({
          ...prev,
          [cardIndex]: subCardIndex
        }))
        setScrollProgress(cardIndex + progressWithinCard)
        
       } else if (scrollTop < sectionStart) {
        // Before section - carousel is visible but not ready for transitions yet
        // Keep showing first card, first sub-card and reset snap index
        currentSnapIndexRef.current = 0
        setActiveCardIndex(0)
        setSubCardIndices(prev => ({
          ...prev,
          0: 0
        }))
        setScrollProgress(0)
      } else {
        // After section - all cards stacked, show last card's last sub-card
        const lastCard = cardsData[cardsData.length - 1]
        const lastSubCardIndex = lastCard.subCards.length - 1
        setActiveCardIndex(cardsData.length - 1)
        setSubCardIndices(prev => ({
          ...prev,
          [cardsData.length - 1]: lastSubCardIndex
        }))
        setScrollProgress(cardsData.length - 1)
      }
    }

    // Initialize snap index on mount
    const snapPoints = calculateSnapPoints()
    if (snapPoints.length > 0) {
      const initialScroll = window.scrollY
      const containerTop = containerRef.current?.offsetTop || 0
      const carouselReadyPoint = containerTop - windowHeight * 0.2
      const sectionStart = carouselReadyPoint + (windowHeight * 0.15)
      
      // If before section start, set to 0 (first card, first sub-card)
      if (initialScroll < sectionStart) {
        currentSnapIndexRef.current = 0
      } else {
        currentSnapIndexRef.current = findNearestSnapIndex(initialScroll, snapPoints)
      }
    }
    
    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('wheel', handleWheel, { passive: false }) // passive: false to allow preventDefault
    
    handleScroll()
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('wheel', handleWheel)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current)
      }
    }
  }, [windowHeight])

  const handleNext = () => {
    if (activeCardIndex < cardsData.length - 1) {
      setActiveCardIndex(activeCardIndex + 1)
    }
  }

  const handlePrev = () => {
    if (activeCardIndex > 0) {
      setActiveCardIndex(activeCardIndex - 1)
    }
  }

  // Handle navigation click - scroll to the clicked card
  const handleNavClick = (cardIndex) => {
    if (!containerRef.current || !windowHeight) return
    
    const containerTop = containerRef.current.offsetTop
    const carouselReadyPoint = containerTop - windowHeight * 0.2
    const sectionStart = carouselReadyPoint + (windowHeight * 0.15)
    const scrollPerSubCard = windowHeight * 0.4
    const cardTransitionBuffer = windowHeight * 0.4
    
    // Calculate the snap point index for the first sub-card of the clicked card
    let snapPointIndex = 0
    for (let i = 0; i < cardIndex; i++) {
      snapPointIndex += cardsData[i].subCards.length
    }
    
    // Calculate the scroll position for this snap point
    let scrollPosition = sectionStart
    
    for (let i = 0; i < cardsData.length; i++) {
      const subCardsPerCard = cardsData[i].subCards.length
      
      if (i < cardIndex) {
        // Add scroll distance for all sub-cards in previous cards
        for (let j = 0; j < subCardsPerCard; j++) {
          scrollPosition += scrollPerSubCard
        }
        // Add buffer after last sub-card if not last card
        if (i < cardsData.length - 1) {
          scrollPosition += cardTransitionBuffer
        }
      } else {
        // We've reached the target card - stop here
        break
      }
    }
    
    // Update state and scroll
    setActiveCardIndex(cardIndex)
    setSubCardIndices(prev => ({
      ...prev,
      [cardIndex]: 0 // Reset to first sub-card
    }))
    setScrollProgress(cardIndex)
    
    isScrollingRef.current = true
    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    })
    
    // Reset scrolling flag after animation completes
    setTimeout(() => {
      isScrollingRef.current = false
    }, 500)
  }

  // Calculate total scroll height based on all sub-cards
  const baseHeight = windowHeight || 800 // Fallback for initial render
  const scrollPerSubCard = baseHeight * 0.4
  const cardTransitionBuffer = baseHeight * 0.4
  const totalSubCards = cardsData.reduce((sum, card) => sum + card.subCards.length, 0)
  const totalScrollHeight = (totalSubCards * scrollPerSubCard) + ((cardsData.length - 1) * cardTransitionBuffer) + baseHeight

  return (
    <div 
      ref={containerRef} 
      className="relative pb-24" 
      style={{ 
        minHeight: `${totalScrollHeight}px`
      }}
    >
      <style>{`
        .cta2-button {
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }
        
        .cta2-button:hover {
          border-color: rgb(94 170 158 / 80%);
        }
        
        .arrow2-icon {
          opacity: 0;
          transform: translateX(-50px);
          transition: all 0.2s ease;
        }
        
        .cta2-button:hover .arrow2-icon {
          opacity: 1;
          transform: translateX(0);
        }
        
        /* Hide scrollbar for tab container */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        .sticky-carousel-container {
          position: sticky;
          left: 0;
          right: 0;
          top: clamp(3rem, 5vh, 2rem);
          z-index: 10;
          min-height: 85vh;
        }
        
        @media (max-width: 640px) {
          .sticky-carousel-container {
            top: clamp(2rem, 4vh, 1.5rem);
            min-height: 80vh;
          }
        }
        
        @media (min-width: 641px) and (max-width: 1024px) {
          .sticky-carousel-container {
            top: clamp(5rem, 4.5vh, 1.75rem);
            min-height: 82.5vh;
          }
        }

        @media (min-width: 1025px) and (max-width: 1600px) {
          .sticky-carousel-container {
            top: clamp(3rem, 4.5vh, 1.75rem);
            min-height: 82.5vh;
          }
        }

        @media (min-width: 1500px) and (max-width: 1800px) {
          .sticky-carousel-container {
            top: clamp(7rem, 4.5vh, 1.75rem);
            min-height: 82.5vh;
          }
        }
        
        @media (min-width: 1800px) {
          .sticky-carousel-container {
            top: clamp(10rem, 5vh, 2rem);
            min-height: 85vh;
          }
        }
      `}</style>
      {/* Title Section - Scrolls away */}
      <div className="max-w-7xl lg:max-w-[1400px] xl:max-w-[1500px] flex items-start mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 xl:mt-24 pt-28 pb-6 sm:pb-7 md:pb-8">
        <div className="text-left flex justify-center w-full">
          <h1 className="text-3xl w-[60%] sm:text-4xl md:text-4xl lg:text-5xl text-center xl:text-4xl font-normal text-gray-900 mb-3 sm:mb-4" style={{ fontFamily: 'Bricolage Grotesque, sans-serif', lineHeight: '1.3' }}>
            Simplifying a Day in the Life of <br/>a Consultant with WhiteOwl
          </h1>
        </div>
      </div>

      {/* Sticky container for Navigation + Cards */}
      <div 
        className="sticky-carousel-container left-0 right-0 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12"
      >
        {/* Main Content: Left Navigation + Right Cards */}
        <div className="max-w-7xl lg:max-w-[1200px] xl:max-w-[1300px] mx-auto flex items-start gap-6 sm:gap-8 md:gap-10 lg:gap-12 xl:gap-16 pt-6 sm:pt-7 md:pt-8">
          {/* Left Navigation Panel */}
          <div className="hidden md:block md:w-48 lg:w-52 xl:w-56 flex-shrink-0">
            <div className="space-y-6 md:space-y-7 lg:space-y-14 mt-10 md:mt-12 lg:mt-14 xl:mt-16">
              {/* Header */}
              <div>
                <h3 className="text-xs md:text-xs lg:text-sm font-semibold text-gray-600 tracking-wide uppercase mb-2" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
                  CONSULTING STAGES
                </h3>
                <p className="text-xs md:text-xs lg:text-sm text-gray-600 leading-relaxed" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
                  where WhiteOwl<br />can help you.
                </p>
              </div>

              
              
              {/* Navigation Steps with conditional line */}
              <div
                className="relative pl-0"
                style={{ height: '140px' }}
              >
                {/* Fixed vertical line spanning full height */}
                <div
                  className="absolute w-0.5 bg-gray-300"
                  style={{
                    top: 0,
                    bottom: 0,
                    left: '12px',
                    transform: 'translateX(-50%)',
                    pointerEvents: 'none',
                  }}
                />

                {/* Navigation Steps - evenly spaced; previous steps shift upward like an abacus */}
                <div className="relative h-full">
                  {cardsData.map((card, index) => (
                    <div
                      key={card.id}
                      className="absolute"
                      style={{
                        top:
                          totalSteps > 1
                            ? `${(index / (totalSteps - 1)) * 100}%`
                            : '50%',
                        left: '12px',
                        transform:
                          // First pagination circle stays fixed at top;
                          // only later "previous" circles move upward to touch it.
                          index < activeCardIndex && index !== 0
                            ? `translate(-50%, -50%) translateY(-${previousOffset}px)`
                            : 'translate(-50%, -50%)',
                        transition:
                          'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      }}
                    >
                      {/* Step Number Circle - Centered on the line */}
                      <div 
                        className={`absolute shadow-xl w-6 h-6 rounded-full flex items-center justify-center font-semibold text-xs border-2 ${
                          index < activeCardIndex
                            ? 'bg-white text-gray-400 border-gray-300'
                            : index === activeCardIndex
                            ? 'bg-gray-900 text-white border-gray-900 scale-105'
                            : 'bg-white text-gray-900 border-gray-300'
                        }`}
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: index < activeCardIndex ? cardsData.length - index : 1,
                          transition:
                            'background-color 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
                            'color 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
                            'border-color 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
                            'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          fontFamily: 'Bricolage Grotesque, sans-serif'
                        }}
                      >
                        {card.step}
                      </div>
                      {/* Step Title - Positioned to the right of the line with proper spacing */}
                      <button
                        onClick={() => handleNavClick(index)}
                        className="absolute text-left transition-all duration-500"
                        style={{
                          left: 'calc(50% + 20px)',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <p 
                          className={`text-xs sm:text-sm uppercase font-semibold ${
                            index < activeCardIndex 
                              ? 'text-gray-400' 
                              : index === activeCardIndex 
                              ? 'text-gray-900' 
                              : 'text-gray-600'
                          }`}
                          style={{ 
                            fontFamily: 'Bricolage Grotesque, sans-serif',
                            margin: 0,
                            padding: 0,
                            transition: 'color 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                          }}
                        >
                          {card.title}
                        </p>
                      </button>
                      
                      {/* Line connector between last previous circle and active */}
                      {/* {index === activeCardIndex - 1 && (
                        <div 
                          className="absolute w-0.5 bg-gray-300 transition-all duration-500"
                          style={{ 
                            height: '16px',
                            top: '23px',
                            left: '12px',
                            transform: 'translateX(-50%)'
                          }}
                        />
                      )} */}
                      
                      {/* Line connector between active and next circle */}
                      {/* {index === activeCardIndex && index < cardsData.length - 1 && (
                        <div 
                          className="absolute w-0.5 bg-gray-300 transition-all duration-500"
                          style={{ 
                            height: '17px',
                            top: '25px',
                            left: '12px',
                            transform: 'translateX(-50%)'
                          }}
                        />
                      )} */}
                      
                      {/* Line connector for future circles (after active) */}
                      {/* {index > activeCardIndex && (
                        <div 
                          className="absolute w-0.5 bg-gray-300 transition-all duration-500"
                          style={{ 
                            height: '16px',
                            top: '-16px',
                            left: '12px',
                            transform: 'translateX(-50%)'
                          }}
                        />
                      )} */}
                    </div>
                  ))}
                </div>
              </div>

              {/* Book a Demo Button with arrow transition */}
              <button
                onClick={() => setShowFreeAccountForm(true)}
                className="cta2-button mt-4 md:mt-5 lg:mt-6 pl-9 pr-1 py-1 md:py-1 bg-[#FFF5F5] border border-blue-500 text-gray-900 text-xs md:text-sm font-light rounded-full hover:bg-white transition-all duration-300 inline-flex items-center justify-center gap-1"
                style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
              >
                <span>Get a free account</span>
                <span className="arrow2-icon inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 bg-gray-900 text-white rounded-full">
                  <FiArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                </span>
              </button>
            </div>
          </div>

          {/* Right Card Stack Section */}
          <div className="flex-1 relative" style={{ minHeight: 'clamp(400px, 60vh, 600px)' }}>
            {/* Card Stack */}
            <div className="relative w-full h-[350px] sm:h-[400px] md:h-[450px] lg:h-[500px] xl:h-[550px]">
            {cardsData.map((card, index) => {
              // Determine card state
              const isActive = index === activeCardIndex
              
              // Calculate transform based on position in stack
              let translateY = 0
              let scale = 1
              let opacity = 1
              let zIndex = cardsData.length - index
              
              // Calculate smooth transitions based on scroll progress
              const futureDistance = windowHeight || 800
              const transitionProgress = scrollProgress - index // Progress for this specific card
              
              if (transitionProgress < -1) {
                // Card is in the future (way below)
                translateY = futureDistance
                scale = 0.92
                opacity = 0
                zIndex = cardsData.length - index
                
              } else if (transitionProgress >= -1 && transitionProgress < 0) {
                // Card is sliding in from bottom (becoming active)
                // Map -1 to 0 into -0.3 to 0 (only slide in during last 30% of previous card's time)
                const adjustedProgress = Math.max(0, (transitionProgress + 0.3) / 0.3) // 0 to 1 during last 30%
                translateY = transitionProgress < -0.3 ? futureDistance : futureDistance * (1 - adjustedProgress)
                scale = transitionProgress < -0.3 ? 0.92 : 0.92 + (0.08 * adjustedProgress)
                // Keep fully opaque when visible to hide previous card content
                opacity = transitionProgress < -0.3 ? 0 : 1
                zIndex = cardsData.length + 1 // Higher z-index for incoming card
                
              } else if (transitionProgress >= 0 && transitionProgress < 1) {
                // Card is sliding out to top (going into stack)
                // Only start transition when progress > 0.7 (during buffer zone)
                const adjustedProgress = Math.max(0, (transitionProgress - 0.7) / 0.3) // 0 to 1 mapping
                translateY = -40 * adjustedProgress
                scale = 1 - (0.05 * adjustedProgress)
                // Keep fully opaque to hide content behind
                opacity = 1
                zIndex = transitionProgress > 0.7 ? cardsData.length - 1 : cardsData.length // Lower z-index only during actual transition
                
              } else {
                // Card is in the past (stacked at top)
                const stackPosition = index - Math.floor(scrollProgress)
                const distanceFromTop = Math.abs(stackPosition)
                translateY = -40 * distanceFromTop // Increased spacing for better separation
                scale = 1 - (0.05 * distanceFromTop) // More visible scale reduction
                opacity = 1
                zIndex = index
              }

              // Get sub-card index for this card from state
              const currentSubCardIndex = subCardIndices[index] || 0
              const currentSubCard = card.subCards[currentSubCardIndex]

              // Get video link from videoData using the explicit videoLink key on the sub-card
              const getVideoLink = () => {
                if (!videoData || !currentSubCard || !currentSubCard.videoLink) return null

                const key = currentSubCard.videoLink
                // Expecting API response shape like: { link1: 'url1', link2: 'url2', ... }
                return videoData[key] || null
              }
              
              const videoLink = getVideoLink()

              return (
                <div
                  key={card.id}
                  className="absolute inset-0"
                  style={{
                    transform: `translateY(${translateY}px) scale(${scale})`,
                    opacity: opacity,
                    zIndex: zIndex,
                    pointerEvents: isActive ? 'auto' : 'none',
                    transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s ease-out, scale 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    willChange: 'transform, opacity'
                  }}
                >
                  <Card 
                    card={card} 
                    isActive={isActive}
                    onNext={handleNext}
                    onPrev={handlePrev}
                    canGoNext={activeCardIndex < cardsData.length - 1}
                    canGoPrev={activeCardIndex > 0}
                    cardContentRef={isActive ? activeCardContentRef : null}
                    activeSubCardIndex={currentSubCardIndex}
                    onSubCardChange={(subCardIndex) => handleSubCardChange(index, subCardIndex)}
                    videoLink={videoLink}
                  />
                </div>
              )
            })}
            </div>
          </div>
        </div>
      </div>
      {/* Free account modal (same pattern as InitialSectionContent) */}
        {showFreeAccountForm && (
          <div
            className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={() => setShowFreeAccountForm(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <EarlyAccessForm />
            </div>
          </div>
        )}
      </div>
  )
}

function Card({ card, isActive, onNext, onPrev, canGoNext, canGoPrev, cardContentRef, activeSubCardIndex, onSubCardChange, videoLink }) {
  const localRef = useRef(null)
  const contentRef = cardContentRef || localRef
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef(null)
  
  const activeSubCard = card.subCards[activeSubCardIndex]
  
  // Auto-play video from start when card becomes active and it's not the first subCard
  useEffect(() => {
    if (isActive && videoRef.current && videoLink && activeSubCardIndex > 0) {
      const playVideo = async () => {
        try {
          // Always restart from the beginning when (re)activating
          videoRef.current.currentTime = 0
          await videoRef.current.play()
          setIsPlaying(true)
        } catch (error) {
          // Auto-play might fail due to browser policies, that's okay
          console.log('Auto-play prevented:', error)
        }
      }
      playVideo()
    } else if (videoRef.current && (!isActive || activeSubCardIndex === 0)) {
      // Pause video when card becomes inactive or when on first subCard
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }, [isActive, activeSubCardIndex, videoLink])
  
  const handlePlayClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const getSolutionLabel = (cardStep, solutionIndex) => {
    const labels = {
      1: ['Lead Signals', 'Account Intelligence', 'Key People'],
      2: ['Assess Opportunity', 'Align Pitch', 'Prepare Decks'],
      3: ['Build Proposals', 'AI Copilot', 'Templatise Output']
    }
    return labels[cardStep]?.[solutionIndex - 1] || `Solution ${solutionIndex}`
  }

  const getSolutionFooter = (cardStep, solutionIndex) => {
    if (solutionIndex !== 3) return null
    const footers = {
      1: '100% relevant signals. Get the right person to contact.',
      2: 'Save 5–10 hours/week',
      3: 'Send 3x more proposals, close deals faster.'
    }
    return footers[cardStep] || null
  }
  
  return (
    <div 
      className="relative rounded-[35px] shadow-2xl overflow-hidden h-[350px] sm:h-[400px] md:h-[450px] lg:h-[500px] xl:h-[550px]" 
      style={{ 
        background: `linear-gradient(90deg, ${card.bgColor} 20%, ${card.bgColor2} 100%)`,
        opacity: 1,
        backgroundColor: card.bgColor // Fallback solid color
      }}
    >
      {/* Top Header Bar */}
      <div className="flex flex-row items-center justify-between gap-3 sm:gap-4 py-4 sm:py-5 md:py-3 pl-2 pr-8 bg-gradient-to-r">
        {/* Left: Step indicator */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => onSubCardChange(0)}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
          >
            <span className="text-white text-normal border-2 border-white rounded-full w-8 h-8 flex items-center justify-center font-medium" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>{card.step}</span>
            <span className="text-white uppercase text-lg font-semibold" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>{card.title}</span>
          </button>
        </div>
        
        {/* Right: Solution Tabs - Only show for active card */}
        {isActive && card.subCards.length > 1 && (
          <div className="flex items-center gap-4 sm:gap-6 overflow-x-auto scrollbar-hide flex-1 justify-end">
            {/* Solution tabs (sub-cards 1, 2, 3...) */}
            {card.subCards.slice(1).map((subCard, idx) => {
              const index = idx + 1 // Actual sub-card index
              return (
                <button
                  key={subCard.id}
                  onClick={() => onSubCardChange(index)}
                  className={`px-2 py-2 text-xs sm:text-sm font-medium transition-all whitespace-nowrap cursor-pointer border-b-2 ${
                    activeSubCardIndex === index
                      ? 'text-white border-white'
                      : 'text-white/60 border-transparent hover:text-white/80'
                  }`}
                  style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                >
                  {getSolutionLabel(card.step, index)}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="relative h-[calc(100%-48px)] sm:h-[calc(100%-56px)] md:h-[calc(100%-64px)] flex">
        {/* Sub-Card Content (No Scroll) */}
        <div className="flex-1 relative overflow-hidden flex flex-col">
          <div ref={contentRef} className="flex-1 overflow-visible">
            <div className="px-4 sm:px-6 md:pl-8 lg:pl-10 xl:pl-12 py-4 sm:py-5 md:py-0 space-y-3 sm:space-y-4">
            {/* First Sub-Card: Only Bullets (Simple Layout) */}
            {activeSubCardIndex === 0 ? (
              <div className="relative flex items-center justify-start min-h-[260px] sm:min-h-[300px] md:min-h-[350px] lg:min-h-[400px]">
                <div className="space-y-4 sm:space-y-5 md:space-y-6">
                  {activeSubCard.bullets.map((bullet, idx) => {
                    const words = bullet.split(' ')
                    const firstWord = words[0]
                    const restOfText = words.slice(1).join(' ')
                    return (
                      <div key={idx} className="flex items-start gap-3 sm:gap-4">
                        {/* <span className="text-white text-lg sm:text-xl mt-0.5 sm:mt-1">•</span> */}
                        <p className="text-white mt-1.5 text-sm sm:text-base md:text-lg leading-relaxed font-medium" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
                          <span className="text-xl sm:text-2xl uppercase md:text-4xl">{firstWord}</span>
                          {restOfText && <span className="text-2xl leading-relaxed font-medium" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}> {restOfText}</span>}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              /* Other Sub-Cards: Split Layout with Content & Video */
              <>
                {/* Content & Video Split Layout */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5 md:gap-6 pt-2 sm:pt-3 md:pt-4 items-start">
                  {/* Left: Description + Keyword pills */}
                  <div className="md:col-span-4 space-y-4 sm:space-y-5">
                    <p
                      className="text-white/90 text-base mt-16 leading-relaxed"
                      style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                    >
                      {activeSubCard.content}
                    </p>

                    {/* Keyword pills styled like floating chips (absolute pattern) */}
                    {activeSubCard.keywords && activeSubCard.keywords.length > 0 && (
                      <div className="relative mt-8 h-32 px-8">
                        {/* Top-left pill */}
                        {activeSubCard.keywords[0] && (
                          <div
                            className="absolute left-4 top-5 inline-flex px-4 py-2 rounded-full bg-white text-gray-900 text-xs sm:text-sm font-medium shadow-md shadow-black/10 -rotate-6"
                            style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                          >
                            {activeSubCard.keywords[0]}
                          </div>
                        )}

                        {/* Middle pill, slightly lower and to the right */}
                        {activeSubCard.keywords[1] && (
                          <div
                            className="absolute left-12 sm:left-16 top-12 sm:top-16 inline-flex px-5 py-2 rounded-full bg-white text-gray-900 text-xs sm:text-sm font-medium shadow-md shadow-black/10 rotate-6"
                            style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                          >
                            {activeSubCard.keywords[1]}
                          </div>
                        )}

                        {/* Bottom pill, aligned more left again */}
                        {activeSubCard.keywords[2] && (
                          <div
                            className="absolute left-7 sm:left-9 top-24 sm:top-28 inline-flex px-4 py-2 rounded-full bg-white text-gray-900 text-xs sm:text-sm font-medium shadow-md shadow-black/10 -rotate-3"
                            style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                          >
                            {activeSubCard.keywords[2]}
                          </div>
                        )}
                      </div>
                    )}

                    {/* {getSolutionFooter(card.step, activeSubCardIndex) && (
                      <div className="pt-4 sm:pt-5 md:pt-6">
                        <p
                          className="text-white text-sm font-extralight"
                          style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                        >
                          {getSolutionFooter(card.step, activeSubCardIndex)}
                        </p>
                      </div>
                    )} */}
                  </div>

                  {/* Right: Video - Larger width, full width and height restricted */}
                  <div className="md:col-span-8">
                    <div className="relative bg-[#1a1a1a] rounded-xl sm:rounded-2xl overflow-hidden w-full h-[280px] sm:h-[320px] md:h-[360px] lg:h-[400px] xl:h-[440px]">
                      {videoLink ? (
                        <>
                          <video
                            ref={videoRef}
                            src={videoLink}
                            className="w-full h-full object-cover"
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onLoadedMetadata={(e) => {
                              // Show first frame by seeking to 0
                              e.target.currentTime = 0.1
                            }}
                          />
                          {/* Play Button Overlay - Only show icon, no watch time */}
                          {!isPlaying && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <button
                                onClick={handlePlayClick}
                                className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all group shadow-lg"
                              >
                                <FaPlay className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white ml-1 sm:ml-1.5 md:ml-2 group-hover:scale-110 transition-transform" />
                              </button>
                            </div>
                          )}
                          {/* Pause button when playing */}
                          {isPlaying && (
                            <button
                              onClick={handlePlayClick}
                              className="absolute inset-0 flex items-center justify-center bg-transparent hover:bg-black/20 transition-all cursor-pointer opacity-0 hover:opacity-100"
                            >
                              <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center">
                                  <div className="w-2 h-8 sm:h-10 md:h-12 bg-white rounded-sm mr-1"></div>
                                  <div className="w-2 h-8 sm:h-10 md:h-12 bg-white rounded-sm"></div>
                                </div>
                              </div>
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Fallback placeholder if no video link */}
                          <div className="relative w-full h-full flex items-center justify-center bg-[#1a1a1a]">
                            <button className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all group shadow-lg">
                              <FaPlay className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white ml-1 sm:ml-1.5 md:ml-2 group-hover:scale-110 transition-transform" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  )
}
